/**
 * Local Draft Service
 * Handles saving and loading draft data to/from local file system
 */

import { v4 as uuidv4 } from 'uuid';

class DraftService {
  constructor() {
    this.basePath = '/save/';
    this.modelsPath = '/save/models/';
  }

  /**
   * Generate unique draft ID
   */
  generateDraftId() {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const uuid = uuidv4().slice(0, 8);
    return `draft_${timestamp}_${uuid}`;
  }

  /**
   * Save draft data to file system
   */
  async saveDraft(draftData, draftName = 'Untitled Draft') {
    try {
      const draftId = this.generateDraftId();
      return await this.saveDraftWithId(draftId, draftData, draftName);
    } catch (error) {
      console.error('Failed to save draft:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Save draft data with a specific ID
   */
  async saveDraftWithId(draftId, draftData, draftName = 'Untitled Draft') {
    try {
      // Create metadata
      const metadata = {
        version: '1.0',
        timestamp: new Date().toISOString(),
        draftName,
        draftId,
        workflowStep: draftData.workflowStep || 'UNKNOWN'
      };

      // Prepare draft file content
      const draftContent = {
        metadata,
        ...draftData
      };

      // Save main draft file
      const draftFileName = `${draftId}.json`;
      const draftPath = this.basePath + draftFileName;

      await this.writeFile(draftPath, JSON.stringify(draftContent, null, 2));

      return {
        success: true,
        draftId,
        fileName: draftFileName,
        path: draftPath
      };
    } catch (error) {
      console.error('Failed to save draft:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Load draft data from file system
   */
  async loadDraft(draftId) {
    try {
      const draftPath = this.basePath + `${draftId}.json`;
      const draftContent = await this.readFile(draftPath);
      const draftData = JSON.parse(draftContent);

      return {
        success: true,
        data: draftData
      };
    } catch (error) {
      console.error('Failed to load draft:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * List all available drafts
   */
  async listDrafts() {
    try {
      // For now, we'll implement a basic version that requires manual tracking
      // In a real implementation, you'd scan the directory
      const drafts = await this.scanDraftDirectory();

      return {
        success: true,
        drafts
      };
    } catch (error) {
      console.error('Failed to list drafts:', error);
      return {
        success: false,
        error: error.message,
        drafts: []
      };
    }
  }

  /**
   * Delete a draft and its associated files
   */
  async deleteDraft(draftId) {
    try {
      // Delete main draft file
      const draftPath = this.basePath + `${draftId}.json`;
      await this.deleteFile(draftPath);

      // Delete associated model files
      const modelFiles = await this.getModelFiles(draftId);
      for (const modelFile of modelFiles) {
        await this.deleteFile(this.modelsPath + modelFile);
      }

      return {
        success: true,
        draftId
      };
    } catch (error) {
      console.error('Failed to delete draft:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Save model file (JSON compact format)
   */
  async saveModelFile(draftId, modelName, content) {
    try {
      const fileName = `${draftId}_${modelName}.json`;
      const filePath = this.modelsPath + fileName;

      await this.writeFile(filePath, content);

      return {
        success: true,
        fileName,
        path: filePath
      };
    } catch (error) {
      console.error('Failed to save model file:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Load model file
   */
  async loadModelFile(draftId, modelName) {
    try {
      const fileName = `${draftId}_${modelName}.json`;
      const filePath = this.modelsPath + fileName;
      const content = await this.readFile(filePath);

      return {
        success: true,
        content,
        fileName
      };
    } catch (error) {
      console.error('Failed to load model file:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Delete model file
   */
  async deleteModelFile(draftId, modelName) {
    try {
      const fileName = `${draftId}_${modelName}.json`;
      const filePath = this.modelsPath + fileName;
      await this.deleteFile(filePath);

      return {
        success: true,
        fileName
      };
    } catch (error) {
      console.error('Failed to delete model file:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Private helper methods for file operations
  // These will be implemented based on the environment

  async writeFile(path, content) {
    try {
      console.log(`[DraftService] Writing file: ${path}`);
      console.log(`[DraftService] Content length: ${content.length}`);

      // Try to write to actual file system using a custom API endpoint
      const response = await fetch('/api/write-file', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          path: path.replace(/^\//, ''), // Remove leading slash
          content: content
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to write file');
      }

      return true;
    } catch (error) {
      // Fallback to localStorage if file system write fails
      console.warn('File system write failed, falling back to localStorage:', error.message);

      const key = `draft_file_${path.replace(/[^a-zA-Z0-9]/g, '_')}`;
      localStorage.setItem(key, content);

      return true;
    }
  }

  async readFile(path) {
    try {
      console.log(`[DraftService] Reading file: ${path}`);

      // Try to read from actual file system
      const response = await fetch('/api/read-file', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          path: path.replace(/^\//, '') // Remove leading slash
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to read file');
      }

      return result.content;
    } catch (error) {
      // Fallback to localStorage if file system read fails
      console.warn('File system read failed, falling back to localStorage:', error.message);

      const key = `draft_file_${path.replace(/[^a-zA-Z0-9]/g, '_')}`;
      const content = localStorage.getItem(key);

      if (!content) {
        throw new Error('File not found');
      }

      return content;
    }
  }

  async deleteFile(path) {
    try {
      console.log(`[DraftService] Deleting file: ${path}`);

      // Try to delete from actual file system
      const response = await fetch('/api/delete-file', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          path: path.replace(/^\//, '') // Remove leading slash
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete file');
      }

      return true;
    } catch (error) {
      // Fallback to localStorage if file system delete fails
      console.warn('File system delete failed, falling back to localStorage:', error.message);

      const key = `draft_file_${path.replace(/[^a-zA-Z0-9]/g, '_')}`;
      localStorage.removeItem(key);

      return true;
    }
  }

  async scanDraftDirectory() {
    try {
      // Try to scan actual file system directory
      const response = await fetch('/api/list-drafts', {
        method: 'GET',
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          return result.drafts.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        }
      }

      // Fallback to localStorage scanning
      console.warn('File system listing failed, falling back to localStorage');

      const drafts = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.match(/^draft_file_.*save.*\.json/)) {
          try {
            const content = localStorage.getItem(key);
            const data = JSON.parse(content);
            if (data.metadata) {
              drafts.push({
                id: data.metadata.draftId,
                name: data.metadata.draftName,
                timestamp: data.metadata.timestamp,
                workflowStep: data.metadata.workflowStep
              });
            }
          } catch (e) {
            console.warn('Failed to parse draft file:', key);
          }
        }
      }

      // Sort by timestamp (newest first)
      drafts.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      return drafts;
    } catch (error) {
      throw new Error(`Failed to scan draft directory: ${error.message}`);
    }
  }

  async getModelFiles(draftId) {
    // Return list of model files for this draft
    const modelFiles = [];
    const pattern = new RegExp(`^draft_file_.*save.*models.*${draftId}_.*\\.json`);

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && pattern.test(key)) {
        // Extract filename from key
        const filename = key.replace(/^draft_file_.*save.*models.*/, '');
        modelFiles.push(filename);
      }
    }

    return modelFiles;
  }
}

// Export singleton instance
export const draftService = new DraftService();
export default draftService;